import { getCookie, setCookie } from "@hooks/cookies";
import { useNavigationContext } from "@hooks/useNavigation";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { Button, IconButton, MenuButton, Row, SplitButton, Tooltip } from "@narmi/design_system";
import { AddEditModal } from "@pages/configuration/messages/saved_replies/AddEditModal";
import { SavedReply } from "@pages/configuration/messages/types";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { forwardRef, useEffect, useState } from "react";
import { $staffApi } from "../../../api";
import { useFileUpload } from "../hooks/useFileUpload";
import { useThread } from "../hooks/useThread";
import { ThreadSearchParams } from "../types";
import { SearchMenuButton } from "./SearchMenuButton";
import styles from "./style.module.css";

export const ActionBar = forwardRef<
  HTMLInputElement,
  {
    setSavedReplyPreview: (reply: SavedReply | null) => void;
    setIsInternalNoteMode: (isInternal: boolean) => void;
  }
>(({ setSavedReplyPreview, setIsInternalNoteMode }, forwardedRef) => {
  const { features, permissions } = useUserConfiguration();

  const { aiDraft, reply, handleUndoAiDraft, aiDraftAvailable, isSubmitting } = useThread();
  const { handleFileChange } = useFileUpload();

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e);
  };

  const aiFeaturesEnabled =
    features.includes("secure_messages_ai") &&
    permissions.includes("backoffice.generate_ai_message");

  return (
    <Row alignItems="center" justifyContent={aiFeaturesEnabled ? undefined : "end"}>
      {aiFeaturesEnabled && (
        <Row.Item>
          <Row alignItems="center" gapSize="s">
            <Row.Item shrink>
              <AiDraftButton />
            </Row.Item>
            {aiDraftAvailable &&
              !aiDraft?.system_generated &&
              aiDraft?.status === "ready" &&
              !reply.value.trim() && (
                <Row.Item shrink>
                  <Tooltip text="Undo draft">
                    <IconButton
                      name="rotate-ccw"
                      kind="action"
                      onClick={() => {
                        handleUndoAiDraft();
                      }}
                    />
                  </Tooltip>
                </Row.Item>
              )}
          </Row>
        </Row.Item>
      )}
      <Row.Item shrink>
        <Row alignItems="center">
          <Row.Item shrink>
            <Row alignItems="center" gapSize="xxs">
              <Row.Item shrink>
                <SavedReplySearch
                  handleReplySelect={(savedReply: SavedReply) => {
                    setSavedReplyPreview(savedReply);
                  }}
                  draftReply={reply.value}
                  disabled={isSubmitting}
                />
              </Row.Item>
              <Row.Item shrink>
                <Tooltip text="Upload files">
                  <input
                    type="file"
                    multiple
                    ref={forwardedRef}
                    style={{ display: "none" }}
                    disabled={isSubmitting}
                    onChange={handleFileInputChange}
                  />
                  <IconButton
                    kind="action"
                    name="paperclip"
                    disabled={isSubmitting}
                    onClick={() =>
                      forwardedRef && typeof forwardedRef !== "function"
                        ? forwardedRef.current?.click()
                        : null
                    }
                  />
                </Tooltip>
              </Row.Item>
            </Row>
          </Row.Item>
          <Row.Item shrink>
            <ReplySplitButton setIsInternalNoteMode={setIsInternalNoteMode} />
          </Row.Item>
        </Row>
      </Row.Item>
    </Row>
  );
});

const AiDraftButton = () => {
  const {
    aiDraft,
    reply,
    handleGenerateReply,
    aiDraftAvailable,
    setIsRegenerating,
    isGeneratingReply,
    isSubmitting,
  } = useThread();

  const isPending = isGeneratingReply || aiDraft?.status === "pending" || isSubmitting;

  const [toolTipOpen, setToolTipOpen] = useState(false);

  const aiDraftInView =
    !reply.value.trim() &&
    aiDraftAvailable &&
    (aiDraft?.status === "ready" || aiDraft?.status === "pending");

  if (reply.value.trim() || aiDraftInView) {
    return (
      <div className={styles.aiIcon}>
        <MenuButton
          renderTrigger={(isOpen: boolean) => {
            return (
              <div
                onMouseEnter={() => setToolTipOpen(true)}
                onMouseLeave={() => setToolTipOpen(false)}
              >
                <Tooltip text="Are you stuck? Explore Narmi AI" isOpen={toolTipOpen && !isOpen}>
                  <Button
                    size="s"
                    kind="ai"
                    disabled={isPending}
                    startIcon="sparkle"
                    onClick={() => {}}
                  />
                </Tooltip>
              </div>
            );
          }}
          footerItem={
            !aiDraftInView ? (
              <MenuButton.Item
                label="Help me write"
                onSelect={() => handleGenerateReply("regenerate")}
              />
            ) : undefined
          }
        >
          {aiDraftInView && (
            <MenuButton.Item label="Regenerate" onSelect={() => setIsRegenerating(true)} />
          )}
          <MenuButton.Item label="Formalize" onSelect={() => handleGenerateReply("formalize")} />
          <MenuButton.Item label="Elaborate" onSelect={() => handleGenerateReply("elaborate")} />
          <MenuButton.Item label="Shorten" onSelect={() => handleGenerateReply("shorten")} />
        </MenuButton>
      </div>
    );
  } else {
    return (
      <Tooltip text="Help me write">
        <Button
          kind="ai"
          startIcon="sparkle"
          onClick={() => handleGenerateReply("regenerate")}
          disabled={isPending}
        />
      </Tooltip>
    );
  }
};

function SavedReplySearch({
  handleReplySelect,
  draftReply,
  disabled = false,
}: {
  handleReplySelect: (reply: SavedReply) => void;
  draftReply: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: savedReplies = { results: [] } } = $staffApi.useQuery(
    "get",
    "/api/saved_replies/",
    {
      params: {
        query: {
          ordering: "name",
          search,
        },
      },
    }
  );

  return (
    <>
      <SearchMenuButton
        trigger={
          <Tooltip text="Saved replies">
            <IconButton kind="action" name="bookmark" disabled={disabled} />
          </Tooltip>
        }
        inputLabel="Search for saved reply"
        onInputChange={setSearch}
        onUserDismiss={() => setSearch("")}
        items={savedReplies.results}
        itemToString={(reply) => (reply ? reply.name : "")}
        renderItem={(reply) => (
          <div className="whiteSpace--truncate" title={reply.body}>
            {reply.name}
          </div>
        )}
        onItemSelect={(reply) => {
          handleReplySelect(reply);
          setSearch("");
        }}
        footerContent={
          draftReply && (
            <div role="button" onClick={() => setIsModalOpen(true)} style={{ cursor: "pointer" }}>
              Convert to saved reply
            </div>
          )
        }
      />
      <AddEditModal
        key={draftReply} // force re-render if the draftReply changes
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        reply={{ body: draftReply }}
      />
    </>
  );
}

function ReplySplitButton({
  setIsInternalNoteMode,
}: {
  setIsInternalNoteMode: (isInternal: boolean) => void;
}) {
  type ReplyMode = "reply" | "reply_and_move" | "internal_note";

  const { onNext } = useNavigationContext<ThreadSearchParams>();
  const { sendNotification } = useNotificationContext();
  const { files, reset: resetFiles } = useFileUpload();
  const { form, reply, isSubmitting, aiDraft, aiDraftAvailable, setAiDraftDismissed } = useThread();

  const [replyMode, setReplyMode] = useState<ReplyMode>(getInitialReplyMode());

  const replyBody = (aiDraftAvailable && !reply.value.trim() ? aiDraft?.body : reply.value)?.trim();
  const aiDraftReady = aiDraftAvailable && aiDraft?.status === "ready";

  useEffect(() => {
    setIsInternalNoteMode(replyMode === "internal_note");

    if (replyMode === "internal_note") {
      if (!reply.value.trim() && aiDraftReady) {
        reply.setValue(aiDraft.body);
        setAiDraftDismissed(true);
      }
    }
  }, [aiDraft?.body, aiDraftReady, reply, replyMode, setIsInternalNoteMode, setAiDraftDismissed]);

  function getInitialReplyMode() {
    const replyModeCookie = getCookie("secure_message_reply_mode");
    if (replyModeCookie === "reply_and_move" && onNext) {
      return "reply_and_move";
    }
    return "reply";
  }

  function isReplyModeSelected(mode: ReplyMode) {
    return replyMode === mode;
  }

  function handleReplyModeSelect(mode: ReplyMode) {
    setCookie("secure_message_reply_mode", mode, "/messages/", 365);
    setReplyMode(mode);
  }

  const postSuccessFn = () => {
    if (onNext && isReplyModeSelected("reply_and_move")) {
      sendNotification({
        type: "success",
        text: "Reply sent!",
      });
      setTimeout(() => {
        onNext();
      }, 500); // Delay navigating to make the transition nicer
    }
  };

  const label = isReplyModeSelected("reply")
    ? "Send"
    : isReplyModeSelected("reply_and_move")
      ? "Send, then next"
      : "Add as internal note";

  return (
    <SplitButton
      kind="primary" // remove: required by NDS bug
      size="s"
      label={label}
      disabled={isSubmitting || (!replyBody && files.length === 0)}
      onClick={() =>
        form.submit({
          files: files.map((file) => file.file),
          onUploaded: resetFiles,
          ...(isReplyModeSelected("internal_note") ? { isInternal: true } : { postSuccessFn }),
        })
      }
    >
      <SplitButton.Menu>
        <SplitButton.MenuItem
          startIcon="" // remove: required by NDS bug
          endIcon={isReplyModeSelected("reply") ? "check" : null}
          label="Send"
          onSelect={() => handleReplyModeSelect("reply")}
        />
        {!!onNext && (
          <SplitButton.MenuItem
            startIcon="" // remove: required by NDS bug
            endIcon={isReplyModeSelected("reply_and_move") ? "check" : null}
            label="Send, then next"
            onSelect={() => {
              handleReplyModeSelect("reply_and_move");
            }}
          />
        )}
        <SplitButton.MenuItem
          startIcon="" // remove: required by NDS bug
          endIcon={isReplyModeSelected("internal_note") ? "check" : null}
          label="Add as internal note"
          onSelect={() => handleReplyModeSelect("internal_note")}
        />
      </SplitButton.Menu>
    </SplitButton>
  );
}
